// Where my own appeals stand.
//
// An appeal is answered by somebody else, some time later, so the one thing
// this has to do is close the loop: who has it, what they said, and — when it
// was approved — what actually changed on the record. Sending a request into
// silence is the failure mode of every approval feature, so a decided appeal
// keeps showing its outcome rather than disappearing the moment it is settled.
//
// Renders nothing when there are no appeals, which is most days for most people.

import { useCallback, useEffect, useState } from 'react';
import { Check, Clock3, FileWarning, X } from 'lucide-react';
import {
  cancelAttendanceAppeal,
  listAttendanceAppeals,
  type AttendanceAppealRow,
} from '../../api/attendance';
import { useToast } from '../../hooks/useToast';
import PersonAvatar from '../people/PersonAvatar';

interface Props {
  reloadKey?: number;
  /** Told when one is withdrawn, so the page can drop it from its own count. */
  onChanged?: () => void;
}

const dayOf = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString([], { day: '2-digit', month: 'short' });
};

/** "23 Sep" for one day, "23 Sep – 25 Sep" when the appeal names a span. */
const spanOf = (row: AttendanceAppealRow) =>
  row.to_date && row.to_date !== row.on_date
    ? `${dayOf(row.on_date)} – ${dayOf(row.to_date)}`
    : dayOf(row.on_date);

const STATUS_STYLE: Record<string, string> = {
  PENDING: 'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200',
  APPROVED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200',
  REJECTED: 'bg-red-100 text-red-700 dark:bg-red-900/60 dark:text-red-200',
  CANCELLED: 'bg-gray-100 text-muted-foreground',
};

const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Waiting',
  APPROVED: 'Approved',
  REJECTED: 'Declined',
  CANCELLED: 'Withdrawn',
};

export default function MyAppeals({ reloadKey = 0, onChanged }: Props) {
  const [rows, setRows] = useState<AttendanceAppealRow[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const { toast } = useToast();

  const load = useCallback(async () => {
    try {
      const { appeals } = await listAttendanceAppeals({ scope: 'mine', days: 90 });
      setRows(appeals);
    } catch {
      setRows([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const withdraw = async (row: AttendanceAppealRow) => {
    setBusyId(row.id);
    try {
      await cancelAttendanceAppeal(row.id);
      setRows((current) =>
        current.map((r) =>
          r.id === row.id ? { ...r, status: 'CANCELLED' as const } : r,
        ),
      );
      toast({ title: 'Appeal withdrawn' });
      onChanged?.();
    } catch (err: any) {
      toast({
        title: "Couldn't withdraw it",
        description: err?.message || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setBusyId(null);
    }
  };

  if (rows.length === 0) return null;

  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <FileWarning className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold text-foreground">My attendance appeals</h3>
      </div>

      <ul className="space-y-2">
        {rows.map((row) => (
          <li
            key={row.id}
            className="rounded-lg border border-border bg-muted/40 p-3"
          >
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-sm font-semibold text-foreground">
                {spanOf(row)}
              </span>
              <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium text-foreground">
                {row.appeal_type_label}
              </span>
              <span
                className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                  STATUS_STYLE[row.status] || STATUS_STYLE.CANCELLED
                }`}
              >
                {STATUS_LABEL[row.status] || row.status}
              </span>
              {row.status === 'PENDING' && (
                <span className="ml-auto flex items-center gap-1 text-[11px] text-muted-foreground">
                  <Clock3 className="h-3 w-3" />
                  <PersonAvatar
                    userId={row.approver_user_id}
                    name={row.approver_name}
                    size={20}
                  />
                  with {row.approver_name || 'your approver'}
                </span>
              )}
            </div>

            <p className="mt-1 text-xs text-muted-foreground">{row.ask}</p>

            {/* What the approval actually did. An "Approved" badge on its own
                would still leave the employee to go and check the timesheet. */}
            {row.status === 'APPROVED' && row.applied_note && (
              <p className="mt-1.5 flex items-start gap-1.5 text-xs text-emerald-800 dark:text-emerald-300">
                <Check className="mt-0.5 h-3 w-3 shrink-0" />
                {row.applied_note}
              </p>
            )}
            {row.status === 'REJECTED' && row.decision_note && (
              <p className="mt-1.5 flex items-start gap-1.5 text-xs text-red-700 dark:text-red-300">
                <X className="mt-0.5 h-3 w-3 shrink-0" />
                {row.decision_note}
              </p>
            )}

            {row.status === 'PENDING' && (
              <button
                type="button"
                onClick={() => withdraw(row)}
                disabled={busyId === row.id}
                className="mt-2 text-xs font-medium text-muted-foreground underline underline-offset-2 hover:text-red-600 disabled:opacity-50"
              >
                {busyId === row.id ? 'Withdrawing…' : 'Withdraw'}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
